/**
 * Rider Remittance — one week, every rider on it.
 *
 * The sheet used to list only the rows an accrual had posted, so a week
 * nobody had accrued showed nobody and sent the owner to an accrual button
 * that did not exist on any screen. Now every rider who belongs on the week is
 * here, whether or not it has been posted:
 *
 *   posted        the figures are facts: due, paid, remaining
 *   Not Accrued   the rider owes the week but it has not been posted; the
 *                 amount is what posting would charge, marked provisional
 *   No Remittance Required   on the week, owing nothing (exempt, or own bike)
 *
 * Who belongs on a week is the Bike Register's answer, given by the API: a
 * rider assigned a registered bike for it. A rider without one is not listed;
 * a charge written before they lost it stays, marked "No bike assigned".
 *
 * Opening the page writes nothing. Posting is a separate button for whoever
 * may decide what is owed.
 *
 * ── One week, everywhere ──────────────────────────────────────────────────
 *
 * There is one piece of state — the date of the week being looked at — and
 * the API answers with the week it belongs to. The dropdown, the arrows, the
 * From and To fields, the heading, every row's due date and the cards all
 * read that one answer, so they cannot disagree. (They did: the week said
 * 12–18 July while From and To both said 19 October, because the date fields
 * were a separate filter over the week list rather than the week itself.)
 */

import { useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, Info, Search } from 'lucide-react';
import {
  BIKE_OWNERSHIPS,
  BIKE_OWNERSHIP_LABELS,
  REMITTANCE_WEEK_STATUSES,
  REMITTANCE_WEEK_STATUS_LABELS,
  VEHICLE_CATEGORIES,
  VEHICLE_CATEGORY_LABELS,
  remittanceWeekLabel,
  shiftRemittanceWeek,
  type RemittanceWeekRow,
  type RemittanceWeekSheet,
  type RemittanceWeekStatus,
  type VehicleCategory,
} from '@cenox/shared';
import { Button, Card, EmptyState, ErrorState, Input, Select, TableSkeleton } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { useAreaFilter, useLocationFilter, useLocationReady } from '../../lib/locationContext';
import { GeoScopeFields } from '../../components/GeoScope';
import { money } from '../../lib/format';
import { ApiError } from '../../lib/api';
import { Stat } from './parts';
import { RemittanceConfig } from './RemittanceConfig';
import { RemittanceWeekPanel, WeekStatusBadge, type PanelMode } from './RemittanceWeekPanel';
import { usePostRemittanceWeek, useRemittanceWeek, useSetRemittanceSettings } from './data';
import { useInvestorOptions } from './investorData';


type StatusFilter = '' | RemittanceWeekStatus | 'overdue';

export function RiderRemittancePage(): ReactNode {
  const { business, can } = useAuth();
  // Country, state and location are the sidebar's — one selection for every screen.
  const area = useAreaFilter();
  const scopeParams = useLocationFilter();

  // The one piece of week state. Undefined is "this week", as the API decides it.
  const [weekOf, setWeekOf] = useState<string | undefined>(undefined);
  const [configCategory, setConfigCategory] = useState<VehicleCategory>('motorcycle');
  const [category, setCategory] = useState<'' | VehicleCategory>('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('');
  const [ownership, setOwnership] = useState<'' | (typeof BIKE_OWNERSHIPS)[number]>('');
  const [investorId, setInvestorId] = useState('');
  const investors = useInvestorOptions();
  const [open, setOpen] = useState<{ riderId: string; mode: PanelMode } | null>(null);

  const sheet = useRemittanceWeek(weekOf, {
    category: category || undefined,
    // The sheet's own geography and the request-wide scope, always agreeing.
    // Filtering never changes a rate: prices are resolved per rider, not per view.
    ...area,
    ...scopeParams,
    ownership: ownership || undefined,
    investorId: investorId || undefined,
  }, useLocationReady());

  const mayCollect = can('remittance:collect');
  const mayManage = can('remittance:manage');

  if (sheet.isError) {
    return <ErrorState message="Could not load the remittance sheet." onRetry={() => void sheet.refetch()} />;
  }
  if (sheet.isPending) return <TableSkeleton rows={6} columns={8} />;

  const data = sheet.data;

  return (
    <div className="space-y-5">
      <RemittanceConfig category={configCategory} onCategoryChange={setConfigCategory} />

      <WeekBar
        data={data}
        loading={sheet.isFetching}
        onSelect={(date) => {
          setWeekOf(date);
          setOpen(null);
        }}
        mayManage={mayManage}
      />

      <SummaryCards data={data} />

      <Filters
        search={search}
        onSearch={setSearch}
        status={status}
        onStatus={setStatus}
        category={category}
        onCategory={setCategory}
        ownership={ownership}
        onOwnership={(v) => {
          setOwnership(v);
          if (v !== 'investor') setInvestorId('');
        }}
        investors={investors.data ?? []}
        investorId={investorId}
        onInvestor={setInvestorId}
      />

      <RiderTable
        data={data}
        search={search}
        status={status}
        mayCollect={mayCollect}
        mayManage={mayManage}
        onOpen={(riderId, mode) => setOpen({ riderId, mode })}
      />

      {open ? (
        <RemittanceWeekPanel
          riderId={open.riderId}
          startsOn={data.week.startsOn}
          mode={open.mode}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}

// ── The week ────────────────────────────────────────────────────────────────

function WeekBar({
  data,
  loading,
  onSelect,
  mayManage,
}: {
  data: RemittanceWeekSheet;
  loading: boolean;
  onSelect: (anyDateInWeek: string) => void;
  mayManage: boolean;
}): ReactNode {
  const { week, weeks, today, policy, graceDays } = data;
  const post = usePostRemittanceWeek();

  // Arrows step by real weeks from the selected one, so a gap in stored
  // periods never strands the reader. The API maps any date to its week.
  const previous = shiftRemittanceWeek(week.startsOn, -1);
  const next = shiftRemittanceWeek(week.startsOn, 1);
  const thisWeek = weeks[0]?.startsOn ?? today;
  const lastWeek = weeks[1]?.startsOn ?? shiftRemittanceWeek(thisWeek, -1);
  const atLatest = week.startsOn >= thisWeek;

  const options = weeks.some((w) => w.startsOn === week.startsOn)
    ? weeks
    : [
        { startsOn: week.startsOn, endsOn: week.endsOn, label: week.label, periodId: week.periodId, posted: week.posted },
        ...weeks,
      ];

  const policyWeekOpen = policy?.applies === true && week.open;

  return (
    <Card>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => onSelect(previous)} aria-label="Previous week">
            <ChevronLeft className="size-4" aria-hidden />
          </Button>
          <div className="w-60">
            <Select
              label="Week"
              value={week.startsOn}
              onChange={(event) => onSelect(event.target.value)}
              options={options.map((w) => ({
                value: w.startsOn,
                label: `${w.label}${w.posted ? '' : ' · not posted'}`,
              }))}
            />
          </div>
          <Button variant="secondary" size="sm" disabled={atLatest} onClick={() => onSelect(next)} aria-label="Next week">
            <ChevronRight className="size-4" aria-hidden />
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" disabled={week.startsOn === thisWeek} onClick={() => onSelect(thisWeek)}>
            This Week
          </Button>
          <Button variant="ghost" size="sm" disabled={week.startsOn === lastWeek} onClick={() => onSelect(lastWeek)}>
            Last Week
          </Button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-end justify-between gap-3 border-t border-slate-100 pt-3">
        <div className="flex items-end gap-2">
          {/*
            From and To ARE the selected week, not a second filter. Choosing a
            date moves to the week that contains it, and both fields then show
            that week's first and last day.
          */}
          <div className="w-40">
            <Input
              label="From"
              type="date"
              value={week.startsOn}
              max={today}
              onChange={(event) => event.target.value && onSelect(event.target.value)}
            />
          </div>
          <div className="w-40">
            <Input
              label="To"
              type="date"
              value={week.endsOn}
              onChange={(event) => event.target.value && onSelect(event.target.value)}
            />
          </div>
        </div>
        <div className="text-right text-xs text-slate-500">
          <p>
            Due {week.dueOn} · overdue after {week.overdueAfter} ({graceDays}-day grace)
          </p>
          <p>Revenue counts when paid, on the payment date</p>
        </div>
      </div>

      {week.open ? (
        <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          This week is still open (it ends {week.endsOn}). Amounts not yet posted are provisional and change as rides are
          completed.
        </p>
      ) : null}

      {policy?.applies ? (
        <p className="mt-2 text-xs text-slate-500">
          {policy.label}. Exactly {policy.threshold} rides qualifies for the lower figure. In force for weeks from{' '}
          {policy.effectiveFrom}.
        </p>
      ) : null}

      {mayManage ? (
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3">
          <Button
            size="sm"
            variant={week.posted ? 'secondary' : 'primary'}
            loading={post.isPending || loading}
            disabled={policyWeekOpen || week.startsOn > today}
            onClick={() => post.mutate(week.startsOn)}
          >
            {week.posted ? 'Refresh ride counts' : "Post this week's charges"}
          </Button>
          <p className="text-xs text-slate-500">
            {policyWeekOpen
              ? `Can be posted from the day after ${week.endsOn}, when the ride count is final.`
              : week.posted
                ? 'Updates the stored ride counts. Posted amounts and manual changes are never repriced; differences are flagged for review.'
                : 'Charges every rider who owes this week. Riders already charged by a payment keep their charge. Revenue counts as money is received.'}
          </p>
          {post.error ? (
            <p role="alert" className="w-full text-xs text-red-700">
              {post.error instanceof ApiError ? post.error.message : 'Could not post the week.'}
            </p>
          ) : null}
          <WeekSettings
            weekStartsOn={data.weekStartsOn}
            graceDays={graceDays}
            investorShare={data.investorWeeklyEntitlement}
            currency={data.currencyCode}
          />
        </div>
      ) : null}
    </Card>
  );
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The remittance calendar: the day weeks start and the grace days before overdue. */
function WeekSettings({
  weekStartsOn,
  graceDays,
  investorShare,
  currency,
}: {
  weekStartsOn: number;
  graceDays: number;
  investorShare: string;
  currency: string;
}): ReactNode {
  const save = useSetRemittanceSettings();
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(String(weekStartsOn));
  const [grace, setGrace] = useState(String(graceDays));
  const [share, setShare] = useState(investorShare);
  const graceValid = /^\d+$/.test(grace) && Number(grace) <= 60;
  const shareValid = /^\d+(\.\d{1,2})?$/.test(share);

  if (!open) {
    return (
      <button type="button" className="ml-auto text-xs font-medium text-blue-700 hover:underline" onClick={() => setOpen(true)}>
        Week starts {WEEKDAYS[weekStartsOn]} · {graceDays}-day grace · investor share {money(investorShare, currency)} ·
        Change
      </button>
    );
  }
  return (
    <form
      className="flex w-full flex-wrap items-end gap-3 rounded-lg border border-slate-200 p-3"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate(
          { weekStartsOn: Number(start), graceDays: Number(grace), investorWeeklyEntitlement: share },
          { onSuccess: () => setOpen(false) },
        );
      }}
    >
      <div className="w-40">
        <Select
          label="Weeks start on"
          value={start}
          onChange={(event) => setStart(event.target.value)}
          options={WEEKDAYS.map((label, value) => ({ value: String(value), label }))}
        />
      </div>
      <div className="w-32">
        <Input
          label="Grace days"
          inputMode="numeric"
          value={grace}
          onChange={(event) => setGrace(event.target.value)}
          error={graceValid ? undefined : '0 to 60'}
        />
      </div>
      <div className="w-44">
        <Input
          label="Investor share a week"
          inputMode="decimal"
          value={share}
          onChange={(event) => setShare(event.target.value)}
          error={shareValid ? undefined : 'An amount'}
          hint="Default, unless a bike has its own"
        />
      </div>
      <p className="min-w-48 flex-1 text-xs text-slate-500">
        Posted weeks keep their dates. A new start day shapes the weeks not yet posted; grace days set when an unpaid
        week becomes overdue. A new investor share applies to weeks charged from now.
      </p>
      {save.error ? (
        <p role="alert" className="w-full text-xs text-red-700">
          {save.error instanceof ApiError ? save.error.message : 'Could not save.'}
        </p>
      ) : null}
      <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(false)}>
        Cancel
      </Button>
      <Button type="submit" size="sm" loading={save.isPending} disabled={!graceValid || !shareValid}>
        Save
      </Button>
    </form>
  );
}

// ── The cards ───────────────────────────────────────────────────────────────

function SummaryCards({ data }: { data: RemittanceWeekSheet }): ReactNode {
  const { totals, currencyCode: currency } = data;
  const forgiven = (Number(totals.waived) + Number(totals.settledWithWaiver) + Number(totals.clearedPart)).toFixed(2);

  return (
    <div>
      {/*
        Accrued first and never netted against anything: what the week charges,
        not revenue — revenue is the money received, on the day it came.
        Provisional sits beside it, labelled, and is never added to it.
        Collected is what was ALLOCATED to this week; received is what came in
        during these dates, whatever week it paid.
      */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Accrued" value={totals.accrued} currency={currency} hint="Charged · revenue when paid" />
        <Stat
          label="Provisional"
          value={totals.provisional}
          currency={currency}
          hint={`${totals.notAccruedRiders} not posted`}
          tone={Number(totals.provisional) > 0 ? 'warn' : 'plain'}
        />
        <Stat label="Collected" value={totals.collected} currency={currency} tone="good" hint="Toward this week" />
        <Stat label="Received" value={totals.receivedInWeek} currency={currency} hint="Paid in these dates" />
        <Stat label="Outstanding" value={totals.outstanding} currency={currency} tone="owed" hint="This week" />
        <Stat
          label="Waivers"
          value={forgiven}
          currency={currency}
          hint={Number(totals.adjustments) !== 0 ? `Charges changed by ${money(totals.adjustments, currency)}` : 'Not revenue'}
        />
        <Stat label="Overdue riders" count={totals.overdueRiders} tone={totals.overdueRiders > 0 ? 'warn' : 'plain'} />
        <Stat label="Earlier weeks" value={totals.arrears} currency={currency} tone="owed" hint="Arrears, kept apart" />
      </div>
      {Number(totals.investorEntitlement) > 0 ? (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat
            label="Investors’ share"
            value={totals.investorEntitlement}
            currency={currency}
            hint={`${money(totals.investorCollected, currency)} collected · ${money(totals.investorOutstanding, currency)} to collect`}
          />
          <Stat
            label="Collected for investors"
            value={totals.investorCollected}
            currency={currency}
            hint="Owed to them · not company revenue"
          />
          <Stat
            label="Company’s share"
            value={totals.companyEntitlement}
            currency={currency}
            hint={`${money(totals.companyOutstanding, currency)} to collect`}
          />
          <Stat label="Company revenue collected" value={totals.companyCollected} currency={currency} tone="good" />
        </div>
      ) : null}
      <p className="mt-1.5 text-xs text-slate-400">
        Cards cover every rider shown for the selected week, location and vehicle type. Search and the status filter
        narrow the table only.
      </p>
    </div>
  );
}

// ── Filters ─────────────────────────────────────────────────────────────────

function Filters(props: {
  search: string;
  onSearch: (v: string) => void;
  status: StatusFilter;
  onStatus: (v: StatusFilter) => void;
  category: '' | VehicleCategory;
  onCategory: (v: '' | VehicleCategory) => void;
  ownership: '' | (typeof BIKE_OWNERSHIPS)[number];
  onOwnership: (v: '' | (typeof BIKE_OWNERSHIPS)[number]) => void;
  investors: Array<{ id: string; name: string }>;
  investorId: string;
  onInvestor: (v: string) => void;
}): ReactNode {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="w-44">
        <Select
          label="Bike owner"
          value={props.ownership}
          onChange={(event) => props.onOwnership(event.target.value as '' | (typeof BIKE_OWNERSHIPS)[number])}
          options={[
            { value: '', label: 'All owners' },
            ...BIKE_OWNERSHIPS.map((o) => ({ value: o, label: BIKE_OWNERSHIP_LABELS[o] })),
          ]}
        />
      </div>
      {props.ownership === 'investor' && props.investors.length > 0 ? (
        <div className="w-48">
          <Select
            label="Investor"
            value={props.investorId}
            onChange={(event) => props.onInvestor(event.target.value)}
            options={[{ value: '', label: 'All investors' }, ...props.investors.map((i) => ({ value: i.id, label: i.name }))]}
          />
        </div>
      ) : null}
      <div className="relative w-56">
        <Input
          label="Search riders"
          value={props.search}
          onChange={(event) => props.onSearch(event.target.value)}
          placeholder="Rider name"
        />
        <Search className="pointer-events-none absolute right-2.5 top-[34px] size-4 text-slate-400" aria-hidden />
      </div>
      <div className="w-48">
        <Select
          label="Payment status"
          value={props.status}
          onChange={(event) => props.onStatus(event.target.value as StatusFilter)}
          options={[
            { value: '', label: 'All statuses' },
            ...REMITTANCE_WEEK_STATUSES.map((s) => ({ value: s, label: REMITTANCE_WEEK_STATUS_LABELS[s] })),
            { value: 'overdue', label: 'Overdue' },
          ]}
        />
      </div>
      <div className="w-44">
        <Select
          label="Vehicle type"
          value={props.category}
          onChange={(event) => props.onCategory(event.target.value as '' | VehicleCategory)}
          options={[
            { value: '', label: 'All vehicle types' },
            ...VEHICLE_CATEGORIES.map((c) => ({ value: c, label: VEHICLE_CATEGORY_LABELS[c].split(' / ')[0]! })),
          ]}
        />
      </div>
      {/* The sidebar's scope, here as well so it can be changed in place. */}
      <div className="grid w-full gap-3 sm:w-auto sm:grid-cols-3">
        <GeoScopeFields bare idPrefix="remittance-scope" />
      </div>
    </div>
  );
}

// ── The riders ──────────────────────────────────────────────────────────────

function RiderTable({
  data,
  search,
  status,
  mayCollect,
  mayManage,
  onOpen,
}: {
  data: RemittanceWeekSheet;
  search: string;
  status: StatusFilter;
  mayCollect: boolean;
  mayManage: boolean;
  onOpen: (riderId: string, mode: PanelMode) => void;
}): ReactNode {
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return data.rows.filter(
      (r) =>
        (!needle || r.riderName.toLowerCase().includes(needle)) &&
        (!status || (status === 'overdue' ? r.overdue : r.status === status)),
    );
  }, [data.rows, search, status]);

  if (data.rows.length === 0) {
    return (
      <EmptyState
        title="No riders for this view"
        description="No active rider matches the selected location and vehicle type, and nobody has a record for this week."
      />
    );
  }

  return (
    <section>
      <div className="mb-2.5 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-900">
          Riders · {remittanceWeekLabel(data.week.startsOn, data.week.endsOn)}
          <span className="ml-1.5 font-normal text-slate-400">
            ({rows.length}
            {rows.length !== data.rows.length ? ` of ${data.rows.length}` : ''})
          </span>
        </h2>
      </div>
      <Card>
        {/* No right padding here: the pinned Actions cell carries its own, so nothing scrolls past it. */}
        <div className="-mx-3 overflow-x-auto pl-3">
          <table className="w-full min-w-[1240px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="py-2 pr-3 font-medium">Rider</th>
                <th className="py-2 pr-3 font-medium">Vehicle</th>
                <th className="py-2 pr-3 font-medium">Week</th>
                <th className="py-2 pr-3 text-right font-medium">Rides</th>
                <th className="py-2 pr-3 text-right font-medium">Due</th>
                <th className="py-2 pr-3 text-right font-medium">Paid</th>
                <th className="py-2 pr-3 text-right font-medium">Remaining</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">Due date</th>
                <th className="py-2 pr-3 font-medium">Last payment</th>
                {/* Pinned to the right so the row's buttons never need a sideways scroll. */}
                <th className="sticky right-0 bg-white py-2 pl-3 pr-3 text-right font-medium shadow-[-6px_0_6px_-6px_rgba(15,23,42,0.15)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <RiderRow
                  key={row.riderId}
                  row={row}
                  currency={data.currencyCode}
                  week={data.week}
                  mayCollect={mayCollect}
                  mayManage={mayManage}
                  onOpen={onOpen}
                />
              ))}
            </tbody>
          </table>
          {rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">No rider matches the search or status.</p>
          ) : null}
        </div>
      </Card>
    </section>
  );
}

function RiderRow({
  row,
  currency,
  week,
  mayCollect,
  mayManage,
  onOpen,
}: {
  row: RemittanceWeekRow;
  currency: string;
  week: RemittanceWeekSheet['week'];
  mayCollect: boolean;
  mayManage: boolean;
  onOpen: (riderId: string, mode: PanelMode) => void;
}): ReactNode {
  const quiet = row.status === 'not_required';
  return (
    <tr className={quiet ? 'text-slate-400' : undefined}>
      <td className="py-2 pr-3">
        <button
          type="button"
          className="text-left font-medium text-slate-900 hover:text-brand-700 hover:underline"
          onClick={() => onOpen(row.riderId, 'view')}
        >
          {row.riderName}
        </button>
        <span className="block text-xs font-normal text-slate-400">
          {row.riderPhone ?? 'No phone'}
          {row.riderStatus !== 'active' ? ` · ${row.riderStatus === 'inactive' ? 'former rider' : row.riderStatus}` : ''}
        </span>
      </td>
      <td className="py-2 pr-3 text-xs text-slate-600">
        {row.vehicleRegistration ?? '—'}
        {row.bikeEligible === false ? (
          <span
            className="block text-[11px] text-amber-700"
            title="Kept as history: this rider has no bike assigned in the Bike Register for the week"
          >
            No bike assigned
          </span>
        ) : null}
        {row.vehicleCategory ? (
          <span className="block text-slate-400">{VEHICLE_CATEGORY_LABELS[row.vehicleCategory].split(' / ')[0]}</span>
        ) : null}
        {row.split.ownership ? (
          <span className="block text-slate-500">
            {row.split.ownership === 'investor'
              ? `Investor${row.split.investorName ? `: ${row.split.investorName}` : ''}`
              : `${BIKE_OWNERSHIP_LABELS[row.split.ownership]}-owned`}
          </span>
        ) : null}
      </td>
      <td className="whitespace-nowrap py-2 pr-3 text-xs text-slate-500">
        {week.startsOn}
        <span className="block">to {week.endsOn}</span>
      </td>
      <td className="py-2 pr-3 text-right tabular-nums">{row.completedRides}</td>
      <td className="py-2 pr-3 text-right tabular-nums">
        {money(row.amountDue, currency)}
        {row.provisional ? <span className="block text-[11px] text-amber-700">provisional</span> : null}
        {row.adjusted ? <span className="block text-[11px] text-slate-400">adjusted</span> : null}
        {Number(row.split.investorEntitlement) > 0 ? (
          <span className="block text-[11px] text-slate-500" title="Investor’s share · company’s share">
            {money(row.split.investorEntitlement, currency)} inv · {money(row.split.companyEntitlement, currency)} co
          </span>
        ) : null}
      </td>
      <td className="py-2 pr-3 text-right tabular-nums text-emerald-700">
        {money(row.amountPaid, currency)}
        {Number(row.split.investorEntitlement) > 0 && Number(row.amountPaid) > 0 ? (
          <span className="block text-[11px] text-slate-500">
            {money(row.split.investorCollected, currency)} inv · {money(row.split.companyCollected, currency)} co
          </span>
        ) : null}
      </td>
      <td className="py-2 pr-3 text-right tabular-nums text-slate-900">{money(row.remaining, currency)}</td>
      <td className="py-2 pr-3">
        <div className="flex flex-col items-start gap-0.5">
          <div className="flex flex-wrap items-center gap-1">
            <WeekStatusBadge status={row.status} />
            {row.overdue ? <WeekStatusBadge status="overdue" /> : null}
            {row.conflicts.length > 0 ? (
              <span title={row.conflicts.join('\n')} className="inline-flex text-amber-600">
                <AlertTriangle className="size-3.5" aria-label="Needs review" />
              </span>
            ) : null}
          </div>
          {row.settlementReason && row.status !== 'paid' ? (
            <span className="max-w-[16rem] truncate text-xs text-slate-400" title={row.settlementReason}>
              {row.settlementReason}
            </span>
          ) : null}
        </div>
      </td>
      <td className="whitespace-nowrap py-2 pr-3 text-xs text-slate-500">{row.dueOn}</td>
      <td className="whitespace-nowrap py-2 pr-3 text-xs text-slate-500">{row.latestPaymentOn ?? '—'}</td>
      <td className="sticky right-0 bg-white py-2 pl-3 pr-3 shadow-[-6px_0_6px_-6px_rgba(15,23,42,0.15)]">
        {/* Stacked until the screen is wide enough to set them side by side. */}
        <div className="flex flex-col items-stretch gap-1 whitespace-nowrap xl:flex-row xl:items-center xl:justify-end xl:gap-1.5">
          <Button variant="ghost" size="sm" onClick={() => onOpen(row.riderId, 'view')}>
            View Details
          </Button>
          {/*
            The three things done to a rider's week, named as the owner names
            them, each opening the details at its form. Add Remittance while the
            week has no charge for this rider (exempt riders excepted); Edit
            Remittance once it has one; Add Payment while money is still owed,
            including on a week not yet posted — the first payment charges it.
          */}
          {mayManage && !row.remittanceId && row.settlementReason === null ? (
            <Button variant="secondary" size="sm" onClick={() => onOpen(row.riderId, 'add')}>
              Add Remittance
            </Button>
          ) : null}
          {mayManage && row.remittanceId && row.status !== 'waived' ? (
            <Button variant="secondary" size="sm" onClick={() => onOpen(row.riderId, 'amount')}>
              Edit Remittance
            </Button>
          ) : null}
          {mayCollect && row.status !== 'waived' && row.status !== 'not_required' && row.remaining !== '0.00' ? (
            <Button size="sm" onClick={() => onOpen(row.riderId, 'pay')}>
              Add Payment
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
